<template>
	<datatable
		v-model:server-options="parameters"
		:headers
		:value="items"
		container-class="workspace-table-container"
		table-class="workspace-table"
		:paginator="false"
		:total
		compact
		@row-click="navigate"
		@update:server-options="(p: Params) => emit('paramsChanged', p)">
		<template #row-icon="{ row }">
			<app-tooltip :text="getStatusIcon(row, statusKey).label" position-x="left" small>
				<div class="inline-flex items-center justify-center h-10">
					<app-svg
						:path="`/icons/catalyst/${getStatusIcon(row, statusKey).icon}.svg`"
						:colour="
							statusKey == 'pick_status' && row.status[0].load_status
								? '#002855'
								: undefined
						"
						:background="getStatusIcon(row, statusKey).colour"
						autoscale />
				</div>
			</app-tooltip>
		</template>

		<template #row-multi_journey="{ row }">
			<div class="relative w-8">
				<app-tooltip
					v-if="row.multi_journey != 0"
					position-x="right"
					:text="row.multi_journey === 1 ? 'Journeys Outstanding' : 'Journeys Completed'">
					<div class="relative">
						<app-icon
							:icon="['fas', 'truck-front']"
							class="text-xl"
							:class="colour(row.multi_journey)" />
						<div
							class="absolute top-0 left-2 flex items-center justify-center rounded-full w-[22px] h-[22px] border-2 border-white text-white font-semibold text-[9px]"
							:class="background(row.multi_journey)">
							+2
						</div>
					</div>
				</app-tooltip>
			</div>
		</template>
	</datatable>
</template>

<script lang="ts" setup>
	import { ref, watch, onMounted, computed, type PropType } from 'vue'
	import { useRouter } from 'vue-router'
	import type { WorkspaceArea } from '@/data/types/area'
	import type { StatusKey } from '@/data/types/contract-status'
	import AppSvg from '@/components/base/AppSvg.vue'
	import { getStatusIcon } from '@/composables/status'
	import type { Params, Row } from '@/data/types/datatable'

	const router = useRouter()

	const props = defineProps({
		params: {
			type: Object as PropType<Params>,
			required: true
		},
		total: {
			type: Number,
			required: true,
			default: 10
		},
		area: {
			type: String as () => WorkspaceArea,
			default: 'stock-check'
		},
		items: {
			type: Object,
			default: {} as Object
		},
		route: {
			type: String,
			default: ''
		},
		statusKey: {
			type: String as () => StatusKey,
			default: 'stock_check_status'
		}
	})

	const headers = computed(() => {
		if (props.area == 'collections') {
			return [
				{ value: 'icon', text: '', props: { width: 45 } },
				{ value: 'collection_number', text: 'Coll No', props: { sortable: true } },
				{ value: 'account', text: 'Acc', props: { sortable: true } },
				{ value: 'collection_date', text: 'Col Date', props: { sortable: true } },
				{ value: 'multi_journey', text: 'Multi Journey', props: { sortable: false } }
			]
		}

		if (props.area == 'journey') {
			return [
				{ value: 'icon', text: '', props: { width: 45 } },
				{ value: 'journey_number', text: 'Jrn No', props: { sortable: true } },
				{ value: 'link_number', text: 'Ref', props: { sortable: true } },
				{ value: 'account_number', text: 'Acc', props: { sortable: true } },
				{ value: 'journey_date', text: 'Jrn Date', props: { sortable: true } }
			]
		}

		return [
			{ value: 'icon', text: '', props: { width: 45 } },
			{ value: 'contract_number', text: 'Cont No', props: { sortable: true } },
			{ value: 'account', text: 'Acc', props: { sortable: true } },
			{ value: 'delivery_date', text: 'Del Date', props: { sortable: true } }
		]
	})

	function colour(multi_journey: number) {
		return multi_journey === 1 ? 'text-red-500' : 'text-green-500'
	}

	function background(multi_journey: number) {
		return multi_journey === 1 ? 'bg-red-500' : 'bg-green-500'
	}

	// Local parameters to attach to the table v-model
	// We will keep this updated with props.params
	const parameters = ref<Params>()

	function navigate(item: Row) {
		let id = item.contract_number

		if (props.area == 'collections') {
			id = item.collection_number
		}

		if (props.area == 'journey') {
			id = item.id
		}

		const route = router.resolve({
			name: props.route,
			params: { id: id }
		})

		const decodedRoute = decodeURIComponent(route.href)

		router.push(decodedRoute)
	}

	const emit = defineEmits(['paramsChanged'])

	// Update local parameters with props.params
	watch(
		props.params,
		(val) => {
			parameters.value = val
		},
		{ deep: true }
	)

	onMounted(() => {
		// initial load - store props.params
		parameters.value = props.params
	})
</script>

<style lang="scss">
	.workspace-table-container {
		.p-datatable-table-container {
			border: 0 !important;
		}

		.workspace-table {
			thead tr {
				border-top: 0;
			}

			thead tr,
			tr {
				border-right: 0;
				border-left: 0;
			}
		}
	}
</style>
